#!/usr/bin/env node
/**
 * skaile — AI asset manager
 *
 * Run `skaile --help` for the full command reference.
 */

import { readFileSync } from "node:fs";
import path from "node:path";
import { fileURLToPath } from "node:url";
import type { DriverTarget } from "@skaile/workspaces/core";
import { Command, Help } from "commander";
import { makeAssetCommand } from "./commands/asset-cmd.ts";
import { makeAuthCommand } from "./commands/auth.ts";
import { makeCacheCommand } from "./commands/cache.ts";
import {
  makeAddCommand,
  makeCreateCommand,
  makeDoctorCommand,
  makeInfoCommand,
  makeListCommand,
  makeRemoveCommand,
  makeSearchCommand,
} from "./commands/catalog.ts";
import { makeCatalogCommand } from "./commands/catalog-cmd.ts";
import { makeCompleteCommand } from "./commands/complete.ts";
import { makeConfigCommand } from "./commands/config.ts";
import { makeConnectCommand } from "./commands/connect.ts";
import { makeConnectorCommand } from "./commands/connector.ts";
import { makeDebugCommand } from "./commands/debug.ts";
import { makeDeployCommand } from "./commands/deploy.ts";
import { makeDiffCommand } from "./commands/diff.ts";
import { makeFlowCommand } from "./commands/flow.ts";
import { makeHistoryCommand } from "./commands/history.ts";
import { makeIntegrationCommand } from "./commands/integration.ts";
import { makeLibraryCommand } from "./commands/library-cmd.ts";
import { makeLogsCommand } from "./commands/logs.ts";
import { makeManageCommand } from "./commands/manage.ts";
import { makeMcpServerCommand } from "./commands/mcp-server.ts";
import { makeNpxCommand } from "./commands/npx.ts";
import { makeOutdatedCommand } from "./commands/outdated.ts";
import { makePatchCommand } from "./commands/patch.ts";
import { makePluginCommand } from "./commands/plugin-registry-cmd.ts";
import { makePresetCommand } from "./commands/preset-cmd.ts";
import { makeCheckCommand, makeCleanCommand, makeInstallCommand } from "./commands/project.ts";
import { makeRebuildCommand } from "./commands/rebuild.ts";
import {
  makeClearCommand,
  makeReplCommand,
  makeResumeCommand,
  makeRunCommand,
  makeStatusCommand,
} from "./commands/run.ts";
import { makeServeCommand } from "./commands/serve.ts";
import { makeSessionCommand } from "./commands/session.ts";
import { makeShowCommand } from "./commands/show.ts";
import { makeSourceCommand } from "./commands/source.ts";
import { makeStoreCommand } from "./commands/store.ts";
import { makeTreeCommand } from "./commands/tree.ts";
import { makeUpdateCommand } from "./commands/update.ts";
import { makeValidateCommand } from "./commands/validate.ts";
import { makeVerifyCommand } from "./commands/verify.ts";
import { makeWhyCommand } from "./commands/why.ts";
import { logErr, logOk, S } from "./helpers.ts";
import { AI_RESOURCES } from "./paths.ts";

const _defaultFormatHelp = Help.prototype.formatHelp;

const pkg = JSON.parse(
  readFileSync(
    path.resolve(path.dirname(fileURLToPath(import.meta.url)), "../../package.json"),
    "utf-8",
  ),
) as { version: string };

const program = new Command("skaile")
  .description("AI asset manager")
  .version(pkg.version, "-v, --version", "Show the skaile CLI version")
  .enablePositionalOptions()
  .configureHelp({
    formatHelp(cmd, helper) {
      if (cmd.name() !== "skaile") return _defaultFormatHelp.call(helper, cmd, helper);
      return `
skaile — AI asset manager

Project:
  init [dir]                   Initialize a project directory (default: .)
  install [--locked]           Install deps (dev mode or CI mode)
  check                        Check for unmet requirements
  clean [--all] [--dry-run]    Remove skaile-managed assets
  setup                        Interactive provider setup wizard
  config show|get|set          Personal settings

Assets (skill, agent, prompt, flow, bundle, contract):
  add <ref> [--global]         Add to skaile.yaml + deploy
  remove <ref> [--global]      Remove from skaile.yaml + undeploy
  list [kind]                  List deployed assets
  show                         Installed assets by domain with sync status
  show <kind> <name>           Print raw asset content
  search [kind] [query]        Search across all repos
  info <ref>                   Metadata, deps, deploy status
  why <ref>                    Show why installed (dep chain)
  tree                         Dependency tree
  update [name]                Re-deploy outdated assets
  outdated [name]              Check for newer versions
  diff <ref>                   Deployed vs source diff
  doctor [name]                Scan for broken deps
  create <name> [--kind <k>]   Scaffold a new asset
  manage                       Interactive TUI (add/remove/sync/patch)
  history [limit]              Recent actions

Patches:
  patch extract <ref>          Extract asset for editing
  patch commit <ref>           Generate .patch file
  patch submit <ref>           Apply to repo + prepare PR
  patch remove <ref>           Remove after upstream merge

Execution:
  run <flow-or-text>           Run a flow or send a single prompt
  repl                         Interactive agent REPL
  resume                       Resume last session
  serve [--compile-test]       Start WebSocket agent server
  mcp-server                   Start workspace MCP server (for .claude/mcp.json)
  integration install [--driver all]    Install agent integration (claude-code | omp | codex)
  integration uninstall [--driver all]  Uninstall agent integration
  integration enable                     Enable integration (claude-code only)
  integration disable                    Disable integration (claude-code only)
  integration status                     Show install / enable state across backends
  plugin install <spec>        Add a registry plugin to skaile.yaml + reconcile
  plugin remove <name>         Remove a registry plugin from skaile.yaml
  plugin list                  List declared plugins + the targets they contribute
  deploy up [--target] [--detach]  Stand the workspace up on a deploy target
  deploy down                  Tear down the active deploy
  deploy status                Show the active deploy's state + ws URL
  deploy logs [--follow]       Stream logs from the active deploy
  debug tools|connectors|mcp|config|state  Inspect running container state
  connect <url>                Interactive WebSocket REPL
  logs [--follow] [--filter]   Tail skaile container logs
  session list|show|switch|del Session management
  status                       Current session state
  clear                        Unset current session

Validation:
  validate [path]              Check manifest schemas + canonical asset names/refs
  validate versions            Check asset version fields and flow pins
  validate changelog           Check changelogs for modified domains

Sources (github repos of AI assets):
  source add <git-url>         Clone + register a github source
  source list / show / remove  Source registry CRUD
  source sync [name]           Re-fetch upstream changes
  source patch / propose       Contribute back via PR
  source manifest *            Sidecar manifest lifecycle

Libraries (your authoring workspaces):
  library init <name>          Create a local library (use --git URL to attach a remote)
  library link <name> <url>    Attach a github remote to a local library
  library create <kind> <name> Scaffold a new asset in a library
  library list / show / status Library inspection
  library default <name>       Set the default library
  library rename / set / remove
  library commit / pull / push / propose / git   Git ops (git backend only)
  library sync <name>          Pull then push (as backend permits)
  library publish / register / yank <ref>        Store publish flow (stubs)

Store (manifest catalog + auth):
  store login / logout / whoami / yank           Auth + admin
  store mode <auto|local|remote>                  Resolution mode (stub)
  store search / show / favourite / unfavourite   Catalog browsing (stub)
  store sync / push                               Local-store sync (stub)

Catalog (debug):
  catalog test / show          Probe a catalog endpoint, print resolved config

Presets:
  preset init [name]           Scaffold a .preset.yaml
  preset validate <path>       Validate a preset file

Advanced:
  flow list|show               Flow browsing
  connector ...                Connector operations
  path                         Show resolved paths

  Run skaile <command> --help for per-command options.
`;
    },
  });

// ── Project ──────────────────────────────────────────────────────────────────

/**
 * Per-backend agent-config defaults emitted by `skaile init`. Keyed by the
 * `--backend` driver target; the values are the bridge driver id (verified
 * against the bridge driver registry) plus the provider/model that apply.
 * omp is model-agnostic, so it carries no provider/model; codex is OpenAI-only
 * with no fixed default model.
 */
const BACKEND_DEFAULTS: Record<
  DriverTarget,
  { driver: string; provider?: string; model?: string }
> = {
  "claude-code": { driver: "claude-sdk", provider: "anthropic", model: "claude-sonnet-4-6" },
  omp: { driver: "omp" },
  codex: { driver: "codex", provider: "openai" },
};

program
  .command("init [project-dir]")
  .description("Initialize a project directory (defaults to current directory)")
  .option("--backend <name>", "Coding-agent backend (claude-code | omp | codex)", "claude-code")
  .option("--no-git", "Skip git init and .gitignore")
  .action((projectDir: string | undefined, opts: { backend: string; git: boolean }) =>
    runInit(projectDir, opts),
  );

/** Top-level `skaile init` handler. Extracted from the command action arrow. */
async function runInit(
  projectDir: string | undefined,
  opts: { backend: string; git: boolean },
): Promise<void> {
  const { mkdirSync, writeFileSync } = await import("node:fs");
  const { stringify } = await import("yaml");
  const { DRIVER_TARGETS, SUPPORTED_DRIVER_TARGETS } = await import("@skaile/workspaces/core");

  const backend = opts.backend as DriverTarget;
  if (!SUPPORTED_DRIVER_TARGETS.includes(backend)) {
    logErr(`Unknown backend "${opts.backend}". Supported: ${SUPPORTED_DRIVER_TARGETS.join(", ")}`);
    process.exitCode = 1;
    return;
  }

  const resolved = path.resolve(projectDir ?? ".");
  const projectName = path.basename(resolved);
  const created: string[] = [];

  const { existsSync } = await import("node:fs");

  if (!existsSync(resolved)) {
    mkdirSync(resolved, { recursive: true });
    created.push(".");
  }

  const skaileDir = path.join(resolved, ".skaile");
  if (!existsSync(skaileDir)) {
    mkdirSync(path.join(skaileDir, "sessions"), { recursive: true });
    created.push(".skaile/");
  }

  const settingsPath = path.join(skaileDir, "settings.json");
  if (!existsSync(settingsPath)) {
    writeFileSync(settingsPath, "{}\n");
    created.push(".skaile/settings.json");
  }

  const wsConfigPath = path.join(resolved, "skaile.yaml");
  if (!existsSync(wsConfigPath)) {
    writeFileSync(wsConfigPath, stringify(buildInitWorkspaceConfig(projectName, backend)));
    created.push("skaile.yaml");
  }

  for (const dir of new Set(Object.values(DRIVER_TARGETS[backend].local))) {
    const full = path.join(resolved, dir);
    if (!existsSync(full)) {
      mkdirSync(full, { recursive: true });
      created.push(`${dir}/`);
    }
  }

  if (opts.git) {
    await initGitScaffold(resolved, created);
  }

  await bootstrapUserConfig();
  await hydrateInitSources(resolved, created);

  printInitSummary(resolved, projectDir, created);
}

/** Build the default `skaile.yaml` document written by `skaile init`. */
function buildInitWorkspaceConfig(projectName: string, backend: DriverTarget) {
  const backendDefaults = BACKEND_DEFAULTS[backend];
  return {
    name: projectName,
    description: `${projectName} AI skill dependencies`,
    "agent-config": {
      default: {
        agent: backend,
        // provider/model are omitted for backends that don't fix them (omp, codex).
        ...(backendDefaults.provider ? { provider: backendDefaults.provider } : {}),
        ...(backendDefaults.model ? { model: backendDefaults.model } : {}),
        driver: backendDefaults.driver,
        max_turns: 200,
      },
    },
  };
}

/** Write `.gitignore` entries and run `git init` for `skaile init` (best-effort). */
async function initGitScaffold(resolved: string, created: string[]): Promise<void> {
  const { execSync } = await import("node:child_process");
  const { existsSync, readFileSync, writeFileSync } = await import("node:fs");

  const gitignorePath = path.join(resolved, ".gitignore");
  const entries = [
    "node_modules/",
    ".skaile/sessions/",
    ".skaile/cache/",
    // skaile-created artifacts that must never be committed: the per-session log
    // DB (SQLite incl. -shm/-wal, ends in .db — not caught by *.log), personal
    // settings (holds API keys), and the install history.
    ".skaile/logs.db*",
    ".skaile/settings.json",
    ".skaile/history.yaml",
    "*.log",
    ".env",
    ".env.local",
  ];
  const existing = existsSync(gitignorePath) ? readFileSync(gitignorePath, "utf-8") : "";
  const have = new Set(existing.split("\n").map((l) => l.trim()));
  const append = entries.filter((e) => !have.has(e));
  if (append.length > 0) {
    const prefix = existing && !existing.endsWith("\n") ? "\n" : "";
    writeFileSync(gitignorePath, `${existing + prefix}${append.join("\n")}\n`);
    if (!existing) created.push(".gitignore");
  }
  if (!existsSync(path.join(resolved, ".git"))) {
    try {
      execSync("git init", { cwd: resolved, stdio: "pipe" });
      created.push(".git/");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.log(`  ${S.dim(`⚠ git init failed: ${msg}`)}`);
    }
  }
}

/** Bootstrap the default user config (`~/.skaile/config.yaml`) when missing. */
async function bootstrapUserConfig(): Promise<void> {
  try {
    const fs = await import("node:fs");
    const { userConfigPath, saveConfig, getConfigDefaults } = await import(
      "@skaile/workspaces/library"
    );
    const userCfgPath = userConfigPath();
    if (!fs.existsSync(userCfgPath)) {
      const defaults = getConfigDefaults();
      saveConfig(userCfgPath, {
        catalog: { url: defaults.catalog.url, cache_ttl: defaults.catalog.cache_ttl },
      });
      console.log(`  ${S.dim(`✓ wrote default user config at ${userCfgPath}`)}`);
    }
  } catch (err) {
    console.log(
      `  ${S.dim(`⚠ could not bootstrap user config: ${err instanceof Error ? err.message : String(err)}`)}`,
    );
  }
}

/** Hydrate sources declared in skaile.yaml — no-op when none. */
async function hydrateInitSources(resolved: string, created: string[]): Promise<void> {
  try {
    const { ensureSourcesCloned } = await import("./ensure-sources.ts");
    const hydrate = ensureSourcesCloned(resolved, { quiet: true });
    for (const n of hydrate.cloned) created.push(`~/.skaile/cache/sources/${n}/`);
    if (hydrate.failed.length > 0) {
      logErr(`Failed to clone source(s): ${hydrate.failed.join(", ")}`);
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.log(`  ${S.dim(`⚠ source hydration skipped: ${msg}`)}`);
  }
}

/** Print the created-files list and the post-init next steps. */
function printInitSummary(
  resolved: string,
  projectDir: string | undefined,
  created: string[],
): void {
  for (const c of created) console.log(`  ${S.dim(`+ ${c}`)}`);
  logOk(`Initialized project at ${S.heading(resolved)}`);
  console.log();
  console.log(S.heading("  Next steps:"));
  const isCurrentDir = resolved === path.resolve(".");
  let step = 1;
  if (!isCurrentDir) {
    console.log(`  ${S.dim(`${step}.`)} ${S.cmd(`cd ${projectDir}`)}`);
    step++;
  }
  console.log(
    `  ${S.dim(`${step}.`)} ${S.cmd("skaile add <ref>")} ${S.dim("— add a dependency and deploy it in one step")}`,
  );
  step++;
  console.log(
    `  ${S.dim(`${step}.`)} ${S.dim("or:")} edit ${S.cmd("skaile.yaml")} to add dependencies, then ${S.cmd("skaile install")}`,
  );
  step++;
  console.log(
    `  ${S.dim(`${step}.`)} ${S.cmd("skaile search <query>")} ${S.dim("— find assets to add")}`,
  );
  console.log();
}

program
  .command("setup")
  .description("Interactive provider setup wizard")
  .action(async () => {
    const { cmdSetup } = await import("./setup.ts");
    await cmdSetup([], { projectDir: process.cwd() });
  });

program.addCommand(makeInstallCommand());
program.addCommand(makeCheckCommand());
program.addCommand(makeCleanCommand());
program.addCommand(makeCacheCommand());
program.addCommand(makeConfigCommand());

// ── Assets ───────────────────────────────────────────────────────────────────

program.addCommand(makeAddCommand());
program.addCommand(makeRemoveCommand());
program.addCommand(makeListCommand());
program.addCommand(makeSearchCommand());
program.addCommand(makeInfoCommand());
program.addCommand(makeShowCommand());
program.addCommand(makeWhyCommand());
program.addCommand(makeTreeCommand());
program.addCommand(makeUpdateCommand());
program.addCommand(makeCatalogCommand());
program.addCommand(makeOutdatedCommand());
program.addCommand(makeDiffCommand());
program.addCommand(makeDoctorCommand());
program.addCommand(makeCreateCommand());
program.addCommand(makeManageCommand());
program.addCommand(makeHistoryCommand());

// ── Patches ──────────────────────────────────────────────────────────────────

program.addCommand(makePatchCommand());

// ── Execution ────────────────────────────────────────────────────────────────

program.addCommand(makeRunCommand());
program.addCommand(makeReplCommand());
program.addCommand(makeResumeCommand());
program.addCommand(makeServeCommand());
program.addCommand(makeMcpServerCommand());
program.addCommand(makeIntegrationCommand());
program.addCommand(makePluginCommand());
program.addCommand(makeDeployCommand());
program.addCommand(makeDebugCommand());
program.addCommand(makeConnectCommand());
program.addCommand(makeLogsCommand());
program.addCommand(makeSessionCommand());
program.addCommand(makeStatusCommand());
program.addCommand(makeClearCommand());

// ── Advanced ─────────────────────────────────────────────────────────────────

program.addCommand(makeFlowCommand(), { hidden: true });
program.addCommand(makeNpxCommand(), { hidden: true });
program.addCommand(makeConnectorCommand());
program.addCommand(makeValidateCommand());
program.addCommand(makeAuthCommand());
program.addCommand(makeRebuildCommand());
program.addCommand(makeVerifyCommand());
program.addCommand(makeStoreCommand());

// ── Sources / Libraries / Store (2026-05-27 vocabulary cleanup) ─────────────

program.addCommand(makeSourceCommand());
program.addCommand(makeLibraryCommand());
program.addCommand(makeAssetCommand());
program.addCommand(makePresetCommand());

program
  .command("path")
  .description("Show resolved paths")
  .action(() => {
    console.log();
    console.log(`  ${S.dim("ai-assets")}    ${S.heading(AI_RESOURCES)}`);
    console.log(`  ${S.dim("patches-dir")}  ${S.heading(".skaile/patches/")}`);
    console.log();
  });

// Complete must be added last
program.addCommand(makeCompleteCommand(program));

await program.parseAsync();
